import { FC } from 'react';

import DefectNietInzetbaarXs from '@/assets/icons/DefectNietInzetbaarXs';
import DefectZandloperXs from '@/assets/icons/DefectZandloperXs';
import { bepaalNietInzetbaarPeriode } from '@/domain/defecten/nietInzetbaarPeriode';
import { Eenheid } from '@/types/werklijnenInline';

import styles from './NietInzetbaarPill.module.css';

type NietInzetbaarPillProps = {
  eenheid: Eenheid | undefined;
  /** Het einde van het venster (mwd-horizon) waarbinnen toekomstige data wordt meegenomen. */
  horizonEinde: Date;
  /** Optioneel, standaard 'new Date()'. Vooral handig om te overschrijven in tests. */
  nu?: Date;
  /**
   * Bepaalt of de "inverse" kleurvarianten worden gebruikt. Standaard true, voor gebruik op een donkere
   * achtergrond (bijv. de legend-tooltip). Zet op false voor gebruik op een lichte achtergrond (bijv. het
   * materieeldetailpaneel), waar de light/dark-kleuren juist omgekeerd moeten zijn.
   */
  inverse?: boolean;
};

/**
 * Toont een pill met de "niet inzetbaar"-status van een eenheid, in 1 van 4 varianten
 * (zie `bepaalNietInzetbaarPeriode`). Rendert niets wanneer de eenheid niet (nu of straks,
 * binnen de horizon) niet-inzetbaar is.
 */
const NietInzetbaarPill: FC<NietInzetbaarPillProps> = ({ eenheid, horizonEinde, nu, inverse = true }) => {
  const periode = bepaalNietInzetbaarPeriode(eenheid, horizonEinde, nu);

  if (!periode) return null;

  const NietInzetbaarIcoon = periode.icoon === 'zandloper' ? DefectZandloperXs : DefectNietInzetbaarXs;

  return (
    <div
      data-testid='niet-inzetbaar-pill'
      className={`${styles.nietInzetbaarPillStyle} ${inverse ? styles.nietInzetbaarPillStyleInverse : styles.nietInzetbaarPillStyleNormal}`}
    >
      <NietInzetbaarIcoon
        aria-hidden='true'
        height='16px'
        width='16px'
        className={inverse ? styles.nietInzetbaarPillIconStyleInverse : styles.nietInzetbaarPillIconStyleNormal}
      />
      {periode.label}
      {periode.overschredenTijdstip && (
        <span
          data-testid='niet-inzetbaar-pill-overschreden-tijdstip'
          className={inverse ? styles.overschredenTijdstipStyleInverse : styles.overschredenTijdstipStyleNormal}
        >
          {periode.overschredenTijdstip}
        </span>
      )}
    </div>
  );
};

export default NietInzetbaarPill;
